import { APP_COLORS } from "../../../utils/helpers";
import { prepareAppPercentageAnalysis } from "../../../utils/MonthlyChartData";
import { normalizeIntegrationKey } from "../../../utils/integrations";

const MONTHS = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const toFiniteNumber = (value) => {
  const number = typeof value === "string" ? Number(value.trim()) : Number(value);
  return Number.isFinite(number) ? number : 0;
};

// The monthly endpoint currently returns "May-2025". The additional accepted
// forms keep this reusable if the endpoint moves to short month or ISO values.
export const parseMonthYear = (value) => {
  if (typeof value !== "string") return null;
  const date = value.trim();
  let year;
  let month;

  const monthFirst = date.match(/^([A-Za-z]+)[-\s/]+(\d{4})$/);
  const yearFirst = date.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);

  if (monthFirst) {
    year = Number(monthFirst[2]);
    month = MONTHS[monthFirst[1].toLowerCase()];
  } else if (yearFirst) {
    year = Number(yearFirst[1]);
    month = Number(yearFirst[2]) - 1;
  }

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 0 || month > 11) {
    return null;
  }

  return {
    key: `${year}-${String(month + 1).padStart(2, "0")}`,
    month,
    year,
    timestamp: Date.UTC(year, month, 1),
  };
};

export const getAppColor = (app) => {
  const normalized = String(app).trim();
  const configuredKey = normalizeIntegrationKey(normalized) || normalized;
  if (APP_COLORS[configuredKey]) return APP_COLORS[configuredKey];

  // Stable fallback: the same unknown app always maps to the same HSL color.
  let hash = 0;
  for (const character of normalized) {
    hash = (hash * 31 + character.codePointAt(0)) >>> 0;
  }
  return `hsl(${hash % 360} 62% 44%)`;
};

const getAppLabel = (app, lang, translations) =>
  translations?.[lang]?.filter?.apps?.[app] ||
  translations?.[lang]?.filter?.appsEn?.[String(app).toLowerCase()] ||
  app;

export const buildDashboardAnalyticsData = (records, lang, translations) => {
  if (!Array.isArray(records)) return null;

  const grouped = new Map();
  const monthDetails = new Map();

  records.forEach((record) => {
    if (!record || typeof record !== "object") return;

    const app = typeof record.app === "string" ? record.app.trim() : "";
    const parsedMonth = parseMonthYear(record.date);
    const hasCount = record.completed !== null && record.completed !== undefined;
    const hasAmount = record.completedAmount !== null && record.completedAmount !== undefined;
    if (!app || !parsedMonth || (!hasCount && !hasAmount)) return;

    monthDetails.set(parsedMonth.key, parsedMonth);
    if (!grouped.has(app)) grouped.set(app, new Map());

    const appMonths = grouped.get(app);
    const current = appMonths.get(parsedMonth.key) || { count: 0, amount: 0 };
    current.count += toFiniteNumber(record.completed);
    current.amount += toFiniteNumber(record.completedAmount);
    appMonths.set(parsedMonth.key, current);
  });

  if (!grouped.size || !monthDetails.size) return null;

  const orderedMonths = [...monthDetails.values()].sort(
    (first, second) => first.timestamp - second.timestamp,
  );
  const spansYears = new Set(orderedMonths.map(({ year }) => year)).size > 1;
  const locale = lang === "ar" ? "ar-SA" : "en-US";
  const months = orderedMonths.map(({ month, year }) =>
    new Intl.DateTimeFormat("en-US", {
      month: "short",
      ...(spansYears ? { year: "numeric" } : {}),
      timeZone: "UTC",
    }).format(new Date(Date.UTC(year, month, 1))),
  );

  const countLabel = translations?.[lang]?.dashboardAnalytics?.count || "Count";
  const amountLabel = translations?.[lang]?.dashboardAnalytics?.amount || "Amount";

  const apps = [...grouped.keys()].sort((a, b) => a.localeCompare(b, locale));
  const lineSeries = [];
  const barSeries = [];

  apps.forEach((app) => {
    const appMonths = grouped.get(app);
    const color = getAppColor(app);
    const label = getAppLabel(app, lang, translations);
    const data = orderedMonths.map(({ key }) => appMonths.get(key) || { count: 0, amount: 0 });

    lineSeries.push({
      id: `${app}-count`,
      label: `${label} — ${countLabel}`,
      data: data.map(({ count }) => count),
      showMark: false,
      color,
      yAxisId: "count",
    });
    barSeries.push({
      id: `${app}-amount`,
      label: `${label} — ${amountLabel}`,
      data: data.map(({ amount }) => amount),
      color,
      yAxisId: "amount",
    });
  });

  return { months, lineSeries, barSeries };
};

export const prepareHorizontalBarAnalysis = (records, lang) => {
  if (!Array.isArray(records)) return null;

  const validRecords = records.filter(
    (record) => record && typeof record.app === "string" && record.app.trim(),
  );
  const percentages = prepareAppPercentageAnalysis(validRecords, lang);
  if (!percentages.length) return null;

  const apps = percentages.map(({ app }) => app);
  const labels = percentages.map(({ label }) => label);

  return {
    labels,
    colors: apps.map(getAppColor),
    serviceCharge: percentages.map(({ serviceValue }) => serviceValue),
    efficiency: percentages.map(({ efficiencyValue }) => efficiencyValue),
  };
};

export const formatMonthFilterValue = ({ month, year }) =>
  new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month, 1)));

export const getDashboardAnalyticsFilterOptions = (records) => {
  if (!Array.isArray(records)) {
    return { apps: [], branches: [], months: [], years: [] };
  }

  const apps = new Set();
  const branches = new Set();
  const monthDetails = new Map();
  const years = new Set();

  records.forEach((record) => {
    if (!record || typeof record !== "object") return;

    if (typeof record.app === "string" && record.app.trim()) apps.add(record.app.trim());
    if (typeof record.branch === "string" && record.branch.trim()) {
      branches.add(record.branch.trim());
    }

    const parsedMonth = parseMonthYear(record.date);
    if (parsedMonth) {
      monthDetails.set(parsedMonth.key, parsedMonth);
      years.add(String(parsedMonth.year));
    }
  });

  return {
    apps: [...apps].sort((a, b) => a.localeCompare(b)),
    branches: [...branches].sort((a, b) => a.localeCompare(b)),
    months: [...monthDetails.values()]
      .sort((a, b) => a.timestamp - b.timestamp)
      .map(formatMonthFilterValue),
    years: [...years].sort((a, b) => Number(a) - Number(b)),
  };
};

export const filterDashboardAnalyticsData = (records, filters) => {
  if (!Array.isArray(records)) return [];

  const selectedApps = filters?.app || [];
  const selectedBranches = filters?.branch || [];
  const selectedMonths = filters?.month || [];
  const selectedYears = filters?.year || [];

  return records.filter((record) => {
    if (!record || typeof record !== "object") return false;

    const app = typeof record.app === "string" ? record.app.trim() : "";
    const branch = typeof record.branch === "string" ? record.branch.trim() : "";
    const parsedMonth = parseMonthYear(record.date);

    if (selectedApps.length && !selectedApps.includes(app)) return false;
    if (selectedBranches.length && !selectedBranches.includes(branch)) return false;
    if (
      selectedMonths.length &&
      (!parsedMonth || !selectedMonths.includes(formatMonthFilterValue(parsedMonth)))
    ) return false;
    if (
      selectedYears.length &&
      (!parsedMonth || !selectedYears.includes(String(parsedMonth.year)))
    ) return false;

    return true;
  });
};
